import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearSelectedLocationAction,
  locationState,
} from "../../app/reducers/location-slice";
import {
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useUploadLocationImagesMutation,
} from "../../query/location-query";
import theme from "../../style/theme";
import { Confirm } from "../../types/confirm";
import { ImageData, urlToImageData } from "../../utils/image/image-data";
import { validateLocationPayload } from "../../utils/validator/location";
import { OutlinedBlackButton } from "../button/OutlinedBlackButton";
import { PostCodeButton } from "../button/PostCodeButton";
import ImageAddLineIcon from "../icons/ImageAddLineIcon/ImageAddLineIcon";
import { LargeLocationImagePreview } from "../panel/LargeLocationImagePreview";
import { GeneralTextField } from "../text-field/GeneralTextField";
import { DialogFooter } from "./DialogFooter";
import { DialogTitle } from "./DialogTitle";
import { LocationImageFormDialog } from "./LocationImageFormDialog";

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface Props {
  setIdPayload: Dispatch<SetStateAction<string>>;
  handleClickLocationDialogClose: () => void;
}

const initLocationForm = {
  address: "",
  longitude: "",
  latitude: "",
};

const initLocationPayloadValidator = {
  address: false,
  // longitude: false,
  // latitude: false,
};

export const LocationFormDialog: React.FC<Props> = ({
  setIdPayload,
  handleClickLocationDialogClose,
}) => {
  const dispatch = useAppDispatch();
  const { selectedLocation } = useAppSelector(locationState);
  const [locationForm, setLocationForm] = useState(initLocationForm);
  const [imageData, setImageData] = useState<ImageData[]>([]);
  const [locationImageDialogOpen, setLocationImageDialogOpen] = useState(false);
  const [locationPayloadValidator, setLocationPayloadValidator] = useState(
    initLocationPayloadValidator
  );
  const createLocationMutation = useCreateLocationMutation();
  const updateLocationMutation = useUpdateLocationMutation();
  const uploadLocationImagesMutation = useUploadLocationImagesMutation();

  const handleClickLocationImageDialogOpen = () => {
    setLocationImageDialogOpen(true);
  };

  const handleClickLocationImageDialogClose = () => {
    setLocationImageDialogOpen(false);
  };

  const handleChangeLocationFormTextField = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLocationForm({
      ...locationForm,
      [e.target.name]: e.target.value,
    });
  };

  const setAddress = (address: string) => {
    setLocationForm({ ...locationForm, address });
  };

  const handleClickCreateLocationUserButton = async () => {
    try {
      const newImageData = await uploadImage();

      const payload = {
        address: locationForm.address,
        longitude:
          locationForm.longitude === ""
            ? undefined
            : Number(locationForm.longitude),
        latitude:
          locationForm.latitude === ""
            ? undefined
            : Number(locationForm.latitude),
        images: newImageData.map((data) => data.url),
      };

      if (validateLocationPayload(payload).hasError) {
        setLocationPayloadValidator(
          validateLocationPayload(payload).validationResults
        );
        toast.error(Confirm.Error);
        return;
      }

      createLocationMutation.mutate(payload, {
        onSuccess: ({ data }) => {
          onSuccess(data.id);
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickUpdateLocationUserButton = async () => {
    try {
      if (!selectedLocation) {
        return;
      }
      const newImageData = await uploadImage();

      const payload = {
        longitude:
          locationForm.longitude === ""
            ? undefined
            : Number(locationForm.longitude),
        latitude:
          locationForm.latitude === ""
            ? undefined
            : Number(locationForm.latitude),
        images: newImageData.map((data) => data.url),
      };

      updateLocationMutation.mutate(
        { id: selectedLocation.id, payload },
        {
          onSuccess: () => {
            onSuccess(selectedLocation.id);
          },
        }
      );
    } catch (error) {
      console.log(error);
    }
  };

  const initForm = () => {
    initImageData();
    if (selectedLocation) {
      setLocationForm({
        address: selectedLocation.road,
        longitude: String(selectedLocation.longitude),
        latitude: String(selectedLocation.latitude),
      });
    } else {
      setLocationForm(initLocationForm);
      // setImageData([]);
    }
  };

  const onSuccess = (id: string) => {
    initForm();
    handleClickCloseButton();
    setIdPayload(id);
  };

  const handleClickCloseButton = () => {
    if (selectedLocation) {
      dispatch(clearSelectedLocationAction());
    } else {
      handleClickLocationDialogClose();
    }
  };

  const getInitImageData = () => {
    if (selectedLocation) {
      return (
        selectedLocation?.images.map((image) => urlToImageData(image)) || []
      );
    }
    return [];
  };

  const initImageData = () => {
    setImageData(getInitImageData());
  };

  const uploadImage = async () => {
    if (imageData.length === 0) {
      return [];
    }
    const payload = new FormData();
    const uploadImageIndex: number[] = [];
    imageData.forEach((data, index) => {
      if (data.file) {
        payload.append("files", data.file);
        uploadImageIndex.push(index);
      }
    });

    const newImageData = Array.from(imageData);
    if (payload.get("files")) {
      await uploadLocationImagesMutation.mutateAsync(payload, {
        onSuccess: ({ data }) => {
          uploadImageIndex.forEach(
            (imageIndex, index) =>
              (newImageData[imageIndex].url = data.images[index])
          );
          setImageData(newImageData);
        },
      });
    }
    return newImageData;
  };

  const handleClickInitButton = () => {
    initForm();
  };

  useEffect(() => {
    initForm();
  }, []);

  return (
    <BootstrapDialog
      onClose={handleClickCloseButton}
      aria-labelledby="customized-dialog-title"
      open={true}
      maxWidth={false}
    >
      <LocationFormDialogContainer>
        <DialogTitle
          title={selectedLocation ? "장소 수정완료" : "신규 장소 등록하기"}
          onClick={handleClickCloseButton}
        />
        <LocationBodyContainer>
          <TopSubTitle>기본 정보</TopSubTitle>
          <TextFieldRaw>
            <TextFieldContainer>주소</TextFieldContainer>
            <GeneralTextFieldContainer>
              <GeneralTextField
                placeholder={"장소를 검색하세요"}
                name={"address"}
                value={locationForm.address}
                onChange={handleChangeLocationFormTextField}
                width="24.125rem"
                isError={locationPayloadValidator.address}
                disabled
              />
            </GeneralTextFieldContainer>
            <PostCodeButtonContainer>
              <PostCodeButton
                disabled={Boolean(selectedLocation)}
                setAddress={setAddress}
              />
            </PostCodeButtonContainer>
          </TextFieldRaw>
          <TextFieldRaw>
            <TextFieldContainer>경도</TextFieldContainer>
            <GeneralTextFieldContainer>
              <GeneralTextField
                placeholder={"자동입력"}
                name={"latitude"}
                value={locationForm.latitude}
                onChange={handleChangeLocationFormTextField}
                width="11.5rem"
                // isError={locationPayloadValidator.latitude}
                disabled={locationForm.address === ""}
              />
            </GeneralTextFieldContainer>
          </TextFieldRaw>
          <TextFieldRaw>
            <TextFieldContainer>위도</TextFieldContainer>
            <GeneralTextFieldContainer>
              <GeneralTextField
                placeholder={"자동입력"}
                name={"longitude"}
                value={locationForm.longitude}
                onChange={handleChangeLocationFormTextField}
                width="11.5rem"
                // isError={locationPayloadValidator.longitude}
                disabled={locationForm.address === ""}
              />
            </GeneralTextFieldContainer>
          </TextFieldRaw>
          <FlexEndContainer>
            <Divider />
          </FlexEndContainer>
          <BodyContainer>
            <BodyLeftContainer>
              <TextFieldContainer>사진첨부</TextFieldContainer>
              <HelperTextFieldContainer>(선택)</HelperTextFieldContainer>
            </BodyLeftContainer>
            <BodyRightContainer>
              {imageData.length > 0 && (
                <LocationImageButtonContainer>
                  <OutlinedBlackButton
                    width="5.5rem"
                    height="2.625rem"
                    text="첨부/수정"
                    onClick={handleClickLocationImageDialogOpen}
                  />
                </LocationImageButtonContainer>
              )}
              <TextFieldContainer>
                {imageData.length}장 첨부완료
              </TextFieldContainer>
              {imageData.length > 0 ? (
                <LargeLocationImagePreview imageData={imageData} />
              ) : (
                <EmptyImageContainer>
                  <ImageAddLineIcon
                    width="2.5rem"
                    height="2.5rem"
                    fill={theme.palette.primitives.gray300}
                  />
                  <EmptyImageText>등록된 사진이 없습니다</EmptyImageText>
                  <OutlinedBlackButton
                    width="5.5rem"
                    height="2.625rem"
                    text="첨부/수정"
                    onClick={handleClickLocationImageDialogOpen}
                  />
                </EmptyImageContainer>
              )}
            </BodyRightContainer>
          </BodyContainer>
          <FlexEndContainer></FlexEndContainer>
        </LocationBodyContainer>

        <DialogFooter
          confirmButtonText={selectedLocation ? "수정완료" : "등록하기"}
          disabled={
            createLocationMutation.isLoading || updateLocationMutation.isLoading
          }
          onClickInitButton={handleClickInitButton}
          onClickConfirmButton={
            selectedLocation
              ? handleClickUpdateLocationUserButton
              : handleClickCreateLocationUserButton
          }
        />
      </LocationFormDialogContainer>

      {locationImageDialogOpen && (
        <LocationImageFormDialog
          imageData={imageData}
          initImageData={initImageData}
          setImageData={setImageData}
          handleClickLocationImageDialogClose={
            handleClickLocationImageDialogClose
          }
        />
      )}
    </BootstrapDialog>
  );
};

const LocationFormDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopSubTitle = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.188rem;
  color: #000000;
  margin-top: 2.25rem;
`;

const TextFieldRaw = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const BodyContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const BodyLeftContainer = styled.div``;

const BodyRightContainer = styled.div`
  margin-left: 3.125rem;
`;

const TextFieldContainer = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
`;

const HelperTextFieldContainer = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray400};
`;

const FlexEndContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Divider = styled.div`
  width: 30.313rem;
  height: 1px;
  background-color: ${theme.palette.primitives.gray200};
  margin: 1.75rem 0;
`;

const GeneralTextFieldContainer = styled.div`
  margin-left: 4.625rem;
`;

const PostCodeButtonContainer = styled.div`
  margin-left: 0.75rem;
`;

const EmptyImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 16.75rem;
  width: 30.313rem;
  background-color: ${theme.palette.primitives.gray50};
  border-radius: 0.25rem;
  margin-top: 1rem;
`;

const EmptyImageText = styled.div`
  font-weight: 500;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: ${theme.palette.primitives.gray400};
  margin: 0.75rem 0 1rem 0;
`;

const LocationImageButtonContainer = styled.div`
  margin-bottom: 1rem;
`;

const LocationBodyContainer = styled.div`
  height: 550px;
  overflow-y: scroll;
  padding: 0 40px 16px;
  width: 36.5rem;
`;
